/**
 * Learning Weights — Pensieve Activation Layer (I-A-10)
 *
 * Pure, deterministic functions for bounded recommendation/signal
 * priority adjustment based on aggregated interaction patterns.
 *
 * Rules:
 *  - All adjustments capped to ±15 points
 *  - Base priority always dominates
 *  - Every adjustment is explainable
 *  - No cross-org blending, no user scoring
 */

// ── Types ──────────────────────────────────────────────

export interface LearningPattern {
  signal_type: string;
  ack_count: number;
  dismiss_count: number;
  drilldown_count: number;
  resolved_after_ack_count: number;
  total_events: number;
  ack_rate: number;    // 0-1
  dismiss_rate: number; // 0-1
}

export interface LearningAdjustment {
  adjustment: number;   // bounded [-15, +15]
  reasons: string[];    // human-readable explanations
}

export interface RecommendationWithLearning {
  base_priority: number;
  learning_adjustment: number;
  final_priority: number;
  learning_reasons: string[];
}

// ── Constants ──────────────────────────────────────────

const MAX_ADJUSTMENT = 15;
const MIN_ADJUSTMENT = -15;

/** Combined (learning + pattern + outcome) cap for I-A-15 feedback loop */
export const MAX_COMBINED_ADJUSTMENT = 25;
export const MIN_COMBINED_ADJUSTMENT = -25;

const ACK_RATE_THRESHOLD = 0.6;
const DISMISS_RATE_THRESHOLD = 0.6;
const RESOLVED_AFTER_ACK_RATIO = 0.5;
const DRILLDOWN_RATE_THRESHOLD = 0.4;

// ── Core computation ───────────────────────────────────

/**
 * Compute a bounded learning adjustment for a given signal type
 * based on aggregated interaction patterns within the caller's scope.
 */
export function computeLearningAdjustment(
  signalType: string,
  patterns: LearningPattern[],
): LearningAdjustment {
  const pattern = patterns.find(
    (p) => p.signal_type === signalType,
  );

  if (!pattern) {
    return { adjustment: 0, reasons: [] };
  }

  let adjustment = 0;
  const reasons: string[] = [];

  // High acknowledgment rate → boost
  if (pattern.ack_rate > ACK_RATE_THRESHOLD) {
    adjustment += 10;
    reasons.push('Similar signals frequently acknowledged in your scope');
  }

  // High resolved-after-ack → boost
  if (
    pattern.ack_count > 0 &&
    pattern.resolved_after_ack_count / pattern.ack_count > RESOLVED_AFTER_ACK_RATIO
  ) {
    adjustment += 5;
    reasons.push('Signals of this type often resolve after acknowledgment');
  }

  // High dismissal rate → reduce
  if (pattern.dismiss_rate > DISMISS_RATE_THRESHOLD) {
    adjustment -= 10;
    reasons.push('Signals of this type are frequently dismissed in your scope');
  }

  // High drilldown rate → boost
  const drilldownRate =
    pattern.total_events > 0
      ? pattern.drilldown_count / pattern.total_events
      : 0;
  if (drilldownRate > DRILLDOWN_RATE_THRESHOLD) {
    adjustment += 5;
    reasons.push('This type often leads to shop investigation');
  }

  // Hard clamp
  adjustment = Math.max(MIN_ADJUSTMENT, Math.min(MAX_ADJUSTMENT, adjustment));

  return { adjustment, reasons };
}

// ── Recommendation helper ──────────────────────────────

/**
 * Enrich recommendations with learning-adjusted priority.
 * When disabled or no patterns, returns original ordering with zero adjustments.
 */
export function applyLearningToRecommendations<
  T extends { type: string; priority_score: number },
>(
  recs: T[],
  patterns: LearningPattern[],
  enabled: boolean,
): (T & RecommendationWithLearning)[] {
  return recs.map((rec) => {
    if (!enabled || patterns.length === 0) {
      return {
        ...rec,
        base_priority: rec.priority_score,
        learning_adjustment: 0,
        final_priority: rec.priority_score,
        learning_reasons: [],
      };
    }

    const { adjustment, reasons } = computeLearningAdjustment(rec.type, patterns);
    return {
      ...rec,
      base_priority: rec.priority_score,
      learning_adjustment: adjustment,
      final_priority: rec.priority_score + adjustment,
      learning_reasons: reasons,
    };
  });
}

/**
 * Compute learning adjustment for a signal type (used in SignalPanel sorting).
 */
export function getSignalLearningAdjustment(
  signalType: string,
  patterns: LearningPattern[],
  enabled: boolean,
): LearningAdjustment {
  if (!enabled || patterns.length === 0) {
    return { adjustment: 0, reasons: [] };
  }
  return computeLearningAdjustment(signalType, patterns);
}
